/**
 * Authentication hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi, ApiError } from '../lib/api';
import { useAppStore } from '../stores/appStore';
import type { User } from '../../shared/types';
import {
  handleLoginError,
  handleLoginSuccess,
  handleVerifyEmailError,
  handleVerifyEmailSuccess,
} from './auth-flow-handlers';

// Query keys
export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
};

/**
 * Hook to get current user
 */
export function useCurrentUser() {
  const setUser = useAppStore((state) => state.setUser);

  return useQuery({
    queryKey: authKeys.me(),
    queryFn: async () => {
      try {
        const response = await authApi.me();
        const user = response.data?.user as User;
        setUser(user);
        return user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          setUser(null);
          return null;
        }
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });
}

/**
 * Hook for user login
 */
export function useLogin(options?: { redirectTo?: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setUser, addNotification, setPendingVerificationEmail } = useAppStore();

  return useMutation({
    mutationFn: (data: { email: string; password: string; mfaCode?: string }) =>
      authApi.login(data),
    onSuccess: (response) =>
      handleLoginSuccess(
        {
          setCachedCurrentUser: (user) => queryClient.setQueryData(authKeys.me(), user),
          navigate,
          setUser,
          setPendingVerificationEmail,
          addNotification,
        },
        response,
        options?.redirectTo
      ),
    onError: (error: ApiError, variables) =>
      handleLoginError(
        {
          setCachedCurrentUser: (user) => queryClient.setQueryData(authKeys.me(), user),
          navigate,
          setUser,
          setPendingVerificationEmail,
          addNotification,
        },
        error,
        variables
      ),
  });
}

/**
 * Hook for user registration
 */
export function useRegister() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setUser, addNotification, setPendingVerificationEmail } = useAppStore();

  return useMutation({
    mutationFn: (data: { email: string; password: string; name: string; website?: string }) =>
      authApi.register(data),
    onSuccess: (response, variables) => {
      if (response.data?.verificationRequired) {
        setPendingVerificationEmail(response.data.email || variables.email);
        addNotification({
          type: 'success',
          message: response.data.message || 'Enviamos um código para seu email.',
          duration: 6000,
        });
        navigate('/verificar-email');
        return;
      }

      const user = response.data?.user as User;
      setUser(user);
      queryClient.setQueryData(authKeys.me(), user);
      addNotification({
        type: 'success',
        message: 'Conta criada com sucesso!',
        duration: 3000,
      });
      navigate('/inicio');
    },
    onError: (error: ApiError) => {
      addNotification({
        type: 'error',
        message: error.message || 'Erro ao criar conta',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook for logout
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { logout, addNotification } = useAppStore();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      logout();
      queryClient.clear();
      addNotification({
        type: 'info',
        message: 'Até logo!',
        duration: 3000,
      });
      navigate('/');
    },
    onError: () => {
      // Logout locally even if API fails
      logout();
      queryClient.clear();
      navigate('/');
    },
  });
}

/**
 * Hook for forgot password
 */
export function useForgotPassword() {
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: (email: string) => authApi.forgotPassword(email),
    onSuccess: () => {
      addNotification({
        type: 'success',
        message: 'Se o email existir, você receberá as instruções de recuperação.',
        duration: 5000,
      });
    },
    onError: (error: ApiError) => {
      addNotification({
        type: 'error',
        message: error.message || 'Erro ao processar solicitação',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook for reset password
 */
export function useResetPassword() {
  const navigate = useNavigate();
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authApi.resetPassword(token, password),
    onSuccess: () => {
      addNotification({
        type: 'success',
        message: 'Senha alterada com sucesso! Faça login com sua nova senha.',
        duration: 5000,
      });
      navigate('/entrar');
    },
    onError: (error: ApiError) => {
      addNotification({
        type: 'error',
        message: error.message || 'Erro ao redefinir senha',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook to update profile
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { setUser, addNotification } = useAppStore();

  return useMutation({
    mutationFn: (data: { name?: string; avatarUrl?: string }) =>
      authApi.updateProfile(data),
    onSuccess: (response) => {
      const user = response.data?.user as User;
      setUser(user);
      queryClient.setQueryData(authKeys.me(), user);
      addNotification({
        type: 'success',
        message: 'Perfil atualizado!',
        duration: 3000,
      });
    },
    onError: (error: ApiError) => {
      addNotification({
        type: 'error',
        message: error.message || 'Erro ao atualizar perfil',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook to change password
 */
export function useChangePassword() {
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      addNotification({
        type: 'success',
        message: 'Senha alterada com sucesso!',
        duration: 3000,
      });
    },
    onError: (error: ApiError) => {
      addNotification({
        type: 'error',
        message: error.message || 'Erro ao alterar senha',
        duration: 5000,
      });
    },
  });
}

/**
 * Hook to verify email with code
 */
export function useVerifyEmail() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setUser, addNotification, setPendingVerificationEmail } = useAppStore();

  return useMutation({
    mutationFn: (data: { email: string; code: string }) =>
      authApi.verifyEmail(data),
    onSuccess: (response) =>
      handleVerifyEmailSuccess(
        {
          setCachedCurrentUser: (user) => queryClient.setQueryData(authKeys.me(), user),
          navigate,
          setUser,
          setPendingVerificationEmail,
          addNotification,
        },
        response
      ),
    onError: (error: ApiError) =>
      handleVerifyEmailError(
        {
          setCachedCurrentUser: (user) => queryClient.setQueryData(authKeys.me(), user),
          navigate,
          setUser,
          setPendingVerificationEmail,
          addNotification,
        },
        error
      ),
  });
}

/**
 * Hook to resend verification code
 */
export function useResendVerification() {
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: (email: string) => authApi.resendVerification(email),
    onSuccess: (response) => {
      addNotification({
        type: 'info',
        message: response.data?.message || 'Código reenviado',
        duration: 4000,
      });
    },
    onError: (error: ApiError) => {
      addNotification({
        type: 'error',
        message: error.message || 'Erro ao reenviar código',
        duration: 5000,
      });
    },
  });
}
